import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import {
  useClaimProfitDropMutation,
  useLazyGetArenaOffersQuery,
} from '@/api/queries/shop';
import { transformArenaOfferReward } from '@/common/lib/transformArenaOfferReward';
import {
  type ArenaOffersDto,
  type ArenaPackRewardDto,
  type BoxReward,
} from '@/common/types/shop/marketBoxes';
import { ModalContext } from '@/components/Modals/Modal/config';
import { type ProfitDropRewardType } from '@/components/OffersMarket/ArenaOffers/config';
import ProfitDropRewardFlipCard from '@/components/OffersMarket/ArenaOffers/ProfitDrop/Rewards/ProfitDropRewardFlipCard';
import { H1, StringSmall } from '@/components/Typography/Typography';
import { changePaymentStatus } from '@/redux/reducers/paymentsReducer';
import {
  changeProfitDropOffer,
  setClaimedReward,
} from '@/redux/reducers/shopReducer';
import { addToBalance, addToStorage } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { MainButton } from '../../Button/Button';
import { MotionPicture } from '../../Picture/Picture';

import ClaimStatusReward from './ClaimStatusReward';

import css from '../style.module.scss';

import purpleLightPng from '/img/shop/arenaOffers/light.png';
import purpleLightWebp from '/img/shop/arenaOffers/light.webp';

const ClaimStatusProcess: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [getShopInfo] = useLazyGetArenaOffersQuery();
  const [claimOffer, { isLoading }] = useClaimProfitDropMutation();
  const { closeModal, secondModal } = useContext(ModalContext);
  const savedRewards = useRef<ArenaPackRewardDto>(
    JSON.parse(localStorage.getItem('profit-drop-rewards') ?? '{}'),
  );
  const [stage, setStage] = useState<
    'hidden' | 'revealed' | 'claiming-animation' | 'claim'
  >('hidden');
  const [rewardArray, setRewardArray] = useState<
    (ArenaPackRewardDto | BoxReward)[]
  >([]);
  const [rewardType, setRewardType] = useState<ProfitDropRewardType>();
  const activeCard = useAppSelector(
    (state) => state.shop.activeProfitDropOffer,
  );
  const [currentRewardIndex, setCurrentRewardIndex] = useState(0);
  const [cardsList, setCardsList] = useState<ProfitDropRewardType[]>([
    'arenaOffer',
    'lottery',
    'tgem',
    'usdt',
  ]);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const index = activeCard?.claimRewardIndex || 0;
    setCurrentRewardIndex(index);
  }, [rewardArray]);

  useEffect(() => {
    if (!activeCard || !activeCard.reward) {
      setRewardArray([]);

      return;
    }

    console.log('ACTIVE CARD CHANGE!');
    console.log(activeCard);

    // expected only one reward
    const reward = activeCard.reward;
    const rewardName = Object.keys(reward)[0] as ProfitDropRewardType;

    if (!reward[rewardName]) return;

    setRewardType(rewardName);

    if (!reward[rewardName]) return;
    setRewardArray(
      rewardName === 'arenaOffer' ? [reward[rewardName]] : [reward],
    );
  }, [activeCard]);

  const currentRewardObject = rewardArray[currentRewardIndex];

  const isLastItem = currentRewardIndex === rewardArray.length - 1;

  const handleFinalAction = async (): Promise<void> => {
    if (!activeCard) return;
    const { balancePayload, storagePayload } = transformArenaOfferReward(
      activeCard.reward,
    );
    dispatch(addToBalance(balancePayload));
    dispatch(addToStorage(storagePayload));
    dispatch(changePaymentStatus(undefined));
    dispatch(changeProfitDropOffer(null));
    await claimOffer({ offerId: activeCard.id }).unwrap();
    await getShopInfo();
    closeModal();
    secondModal.closeModal();
  };

  const handleNextClick = useCallback(() => {
    if (isLoading) return;

    if (isLastItem) {
      handleFinalAction();
    } else {
      const index = currentRewardIndex + 1;
      setCurrentRewardIndex(index);
      dispatch(setClaimedReward(index));
    }
  }, [isLastItem, activeCard, isLoading]);

  useEffect(() => {
    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!activeCard) return null;

  const handleCardClick = (index: number): void => {
    if (cardsList[index] !== rewardType) {
      const newValue = [...cardsList];
      const prevValue = newValue[index];
      const rewardIndex = newValue.indexOf(rewardType!);
      newValue[index] = rewardType!;
      newValue[rewardIndex] = prevValue;
      setCardsList(newValue);
    }
    setStage('revealed');
  };

  if (!currentRewardObject) return null;

  const getCardStatus = (
    type: string,
  ): 'hidden' | 'revealed-nonreward' | 'revealed-reward' =>
    stage === 'hidden'
      ? 'hidden'
      : rewardType === type
        ? 'revealed-reward'
        : 'revealed-nonreward';
  const getValueType = (type: string): number => {
    if (type === 'arenaOffer') return 0;

    if (type === 'lottery') {
      return (
        activeCard.reward.lottery?.goldTicket ??
        savedRewards.current.lottery?.goldTicket ??
        0
      );
    }

    if (type === 'tgem')
      return activeCard.reward.tgem ?? savedRewards.current.tgem ?? 1;

    if (type === 'usdt')
      return activeCard.reward.usdt ?? savedRewards.current.usdt ?? 1;

    return 1;
  };

  const handleFinishFlip = (): void => {
    timeoutRef.current = setTimeout(() => {
      setStage('claiming-animation');
    }, 1500);
  };

  return (
    <AnimatePresence>
      {stage !== 'claim' && (
        <MotionPicture
          img={purpleLightPng}
          webp={purpleLightWebp}
          alt="light"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[-1] w-[35.75rem]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
      )}
      <div className={css.title}>
        <H1>{stage !== 'claim' ? t('Big Drop 1 of 4') : t('Congrats!')}</H1>
        <p>
          {stage === 'hidden'
            ? t('Select one of four awards')
            : stage === 'revealed'
              ? t('Receive one of four awards')
              : t('Your reward.')}
        </p>
      </div>
      <motion.div>
        {['hidden', 'revealed'].includes(stage) && (
          <motion.div
            key={'cards-list'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-[8rem_8rem] gap-2 mt-12"
          >
            {cardsList.map((card, i) => {
              const status = getCardStatus(card);
              const isFinalReward = rewardType === card;

              return (
                <ProfitDropRewardFlipCard
                  layoutId={i.toString()}
                  key={'card' + i.toString()}
                  onClick={() => handleCardClick(i)}
                  type={card}
                  value={getValueType(card)}
                  status={status}
                  onFinish={handleFinishFlip}
                  delay={isFinalReward ? 0 : 0.5 + i * 0.1}
                  size="lg"
                />
              );
            })}
          </motion.div>
        )}

        {stage === 'claiming-animation' && rewardType && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center gap-10 mt-12 720-h:gap-6 720-h:mt-8"
          >
            <div className="size-44">
              <ProfitDropRewardFlipCard
                key="claiming"
                layoutId={cardsList.indexOf(rewardType).toString()}
                type={rewardType}
                value={getValueType(rewardType)}
                status={'revealed-reward'}
                size="lg"
              />
            </div>
            <MainButton
              onClick={() => setStage('claim')}
              text={t('Receive award')}
              className="!w-[12.5rem]"
              classNamesText="!text-lg"
            />
          </motion.div>
        )}
      </motion.div>

      {stage === 'claim' && rewardType && (
        <ClaimStatusReward
          offer={activeCard}
          isLast={isLastItem}
          type={rewardType}
          currentRewardIndex={currentRewardIndex}
          onNext={handleNextClick}
          rewardArray={rewardArray}
          rewardKey={
            Object.keys(activeCard.reward)[
              currentRewardIndex
            ] as keyof ArenaOffersDto
          }
        />
      )}

      {stage === 'hidden' && (
        <StringSmall className="!text-white/60 mt-12">
          {t('Tap to open')}
        </StringSmall>
      )}
    </AnimatePresence>
  );
};

export default ClaimStatusProcess;
